import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import * as THREE from 'three';
import { ALL_ENTITY_KINDS, ENTITY_KIND_ROAD, ASSET_MODE_PER_ENTITY, ASSET_MODE_PER_CELL, ENTITY_KIND_PARK } from './world';
import { baseName } from './file-utils';

function _stringCompare(a, b) {
  if (a > b) {
    return 1;
  } else if (a < b) {
    return -1;
  } else {
    return 0;
  }
}

export default class AssetLibrary {
  static isValidName(name) {
    const NAME_RE = /[a-z]+/;
    return NAME_RE.exec(name);
  }

  static willBeImported(name) {
    const nameParts = name.split('_');
    return ALL_ENTITY_KINDS.includes(nameParts[0]);
  }

  static async _loadGLB(url, onProgress) {
    const loader = new GLTFLoader();
    return await new Promise((resolve) => {
      loader.load(
        url,
        (gltf) => {
          gltf.scene.traverse((child) => {
            if (child.isMesh) child.castShadow = child.receiveShadow = true;
          });
          resolve(gltf.scene);
        },
        onProgress
      );
    });
  }

  static fromJSONWithoutGLB(json) {
    const library = new AssetLibrary();
    library.name = json.name;
    library.filename = json.filename;
    library.url = json.url;
    library.assets = json.assets;
    library._createVariantMap();
    return library;
  }

  static createErrorMesh() {
    const geometry = new THREE.BoxGeometry();
    const material = new THREE.MeshBasicMaterial({ color: 0xff00ff });
    const mesh = new THREE.Mesh(geometry, material);
    return mesh;
  }

  static _setAssetPropDefault(asset, key, defaultValue = 0) {
    const value = asset[key];
    if (value === undefined) {
      asset[key] = defaultValue;
    }
  }

  // Load a JSON library.
  static async fromJSON(json, local = false, onProgress = undefined) {
    const library = AssetLibrary.fromJSONWithoutGLB(json);
    let glbUrl;
    if (local) {
      glbUrl = `/asset-libraries/${library.filename}`;
    } else {
      glbUrl = library.url;
    }
    library.scene = await AssetLibrary._loadGLB(glbUrl, onProgress);

    // Link meshes to assets.
    for (const asset of library.assets) {
      const scene = library.scene;
      console.assert(scene, `${library.name}: Could not find scene ${asset.filename} for asset ${asset}.`);
      console.assert(scene.type === 'Group', `Top-level scene object should be a Group.`);
      let child = scene.children.find((child) => child.name === asset.name);
      if (!child) {
        console.error(`${library.name}: Could not find scene child named ${asset.name} in ${library.filename}.`);
        child = this.createErrorMesh();
      }
      if (child.type !== 'Mesh' && child.type !== 'Group') {
        console.error(`${library.name}: Child ${asset.name} should be a Mesh or Group.`);
        child = this.createErrorMesh();
      }
      asset.mesh = child;
      if (asset.kind === ENTITY_KIND_ROAD) {
        this._setAssetPropDefault(asset, 'pedestrians');
        this._setAssetPropDefault(asset, 'bikes');
        this._setAssetPropDefault(asset, 'cargoBikes');
        this._setAssetPropDefault(asset, 'cars');
        this._setAssetPropDefault(asset, 'trucks');
        this._setAssetPropDefault(asset, 'buses');
        this._setAssetPropDefault(asset, 'passengerTrains');
        this._setAssetPropDefault(asset, 'freightTrains');
      }
    }
    library._createVariantMap();
    return library;
  }

  // Load in a GLB file and create a new library based on its input.
  static async fromGLB(url) {
    const filename = decodeURIComponent(new URL(url).pathname).split('/').reverse()[0];
    const scene = await AssetLibrary._loadGLB(url);
    const assets = [];
    for (const child of scene.children) {
      if (child.type !== 'Mesh' && child.type !== 'Group') continue;

      const nameParts = child.name.split('_');
      let kind = 'unknown';
      if (ALL_ENTITY_KINDS.includes(nameParts[0])) {
        kind = nameParts[0];
      } else {
        continue;
      }

      const box = new THREE.Box3();
      const size = new THREE.Vector3();
      box.expandByObject(child);
      box.getSize(size);
      const width = Math.round(size.x / 5);
      const height = Math.round(size.z / 5);

      let renderMode = ASSET_MODE_PER_ENTITY;
      if (kind === ENTITY_KIND_ROAD || kind === ENTITY_KIND_PARK) {
        renderMode = ASSET_MODE_PER_CELL;
      }
      const asset = {
        kind,
        name: child.name,
        width,
        height,
        renderMode,
        mesh: child,
      };
      if (kind === ENTITY_KIND_ROAD) {
        asset.pedestrians = 0;
        asset.bikes = 0;
        asset.cargoBikes = 0;
        asset.cars = 0;
        asset.trucks = 0;
        asset.buses = 0;
        asset.passengerTrains = 0;
        asset.freightTrains = 0;
      }
      assets.push(asset);
    }
    const library = new AssetLibrary();
    library.name = baseName(filename);
    library.filename = filename;
    library.url = url;
    library.assets = assets;
    library._createVariantMap();
    return library;
  }

  constructor() {
    this.name = '';
    this.filename = '';
    this.url = '';
    this.assets = [];
    this.scene = undefined;
  }

  toJSON() {
    const json = {};
    json.name = this.name;
    json.filename = this.filename;
    json.url = this.url;
    json.assets = JSON.parse(JSON.stringify(this.assets));
    json.assets.forEach((asset) => {
      delete asset.mesh;
    });
    return json;
  }

  merge(newAssetLibrary) {
    this.filename = newAssetLibrary.filename;
    this.url = newAssetLibrary.url;
    let newAssetNames = new Set(newAssetLibrary.assets.map((asset) => asset.name));
    // Remove all assets that do not appear in the new library.
    this.assets = this.assets.filter((asset) => newAssetNames.has(asset.name));
    // From the new library, remove assets that appear in the old library.
    // We want to keep existing properties.
    this.assets.forEach((asset) => newAssetNames.delete(asset.name));
    // Push new assets that appear in the new library but not in the old library.
    const newAssets = Array.from(newAssetNames).map((n) => newAssetLibrary.assets.find((a) => a.name === n));
    this.assets.push(...newAssets);
    // Sort all assets by name.
    this.assets.sort((a, b) => _stringCompare(a.name, b.name));
    this._createVariantMap();
  }

  deleteAssetByName(name) {
    this.assets = this.assets.filter((a) => a.name !== name);
  }

  _createVariantMap() {
    this.variants = {};
    const baseAssets = this.assets.filter((asset) => !asset.name.includes('@'));
    for (const baseAsset of baseAssets) {
      this.variants[baseAsset.name] = this.assets.filter(
        (asset) => asset.name.includes('@') && asset.name.split('@')[0] === baseAsset.name
      );
    }
  }
}
